/**
 * Centralized spacing configuration for landing page sections and blocks.
 * Provides preset spacing levels that automatically scale across all device breakpoints.
 */

import type { GlobalSpacingConfig } from "@/types/config";

export type SpacingPreset = "tight" | "normal" | "spacious" | "extra-spacious";

export interface SpacingClasses {
  section: {
    vertical: string; // py-* classes for section padding top/bottom
    horizontal: string; // px-* classes for section padding left/right
  };
  block: {
    internal: string; // space-y-* classes for spacing between block elements
    container: string; // max-width and centering classes
    paddingX: string; // px-* classes for block internal horizontal padding
    paddingY: string; // py-* classes for block internal vertical padding
  };
}

/**
 * Preset spacing mappings that scale responsively across breakpoints.
 *
 * Breakpoints:
 * - base (mobile): < 640px
 * - sm (small tablet): >= 640px
 * - md (tablet): >= 768px
 * - lg (desktop): >= 1024px
 * - xl (large desktop): >= 1280px
 */
export const SPACING_PRESETS: Record<SpacingPreset, SpacingClasses> = {
  tight: {
    section: {
      vertical: "py-8 sm:py-12 lg:py-16",
      horizontal: "px-4 sm:px-6 lg:px-8 xl:px-10",
    },
    block: {
      internal: "space-y-6 md:space-y-8 lg:space-y-10",
      container: "max-w-7xl mx-auto",
      paddingX: "px-4 sm:px-6 lg:px-8",
      paddingY: "py-6 sm:py-8 lg:py-10",
    },
  },
  normal: {
    section: {
      vertical: "py-12 sm:py-16 lg:py-20 xl:py-24",
      horizontal: "px-4 sm:px-6 lg:px-8 xl:px-12",
    },
    block: {
      internal: "space-y-8 md:space-y-12 lg:space-y-16",
      container: "max-w-7xl mx-auto",
      paddingX: "px-4 sm:px-6 lg:px-8",
      paddingY: "py-8 sm:py-10 lg:py-12",
    },
  },
  spacious: {
    section: {
      vertical: "py-16 sm:py-20 lg:py-28 xl:py-32",
      horizontal: "px-4 sm:px-6 lg:px-10 xl:px-16",
    },
    block: {
      internal: "space-y-10 md:space-y-14 lg:space-y-20",
      container: "max-w-7xl mx-auto",
      paddingX: "px-6 sm:px-8 lg:px-10",
      paddingY: "py-10 sm:py-12 lg:py-16",
    },
  },
  "extra-spacious": {
    section: {
      vertical: "py-20 sm:py-28 lg:py-36 xl:py-44",
      horizontal: "px-6 sm:px-8 lg:px-12 xl:px-20",
    },
    block: {
      internal: "space-y-12 md:space-y-16 lg:space-y-24",
      container: "max-w-7xl mx-auto",
      paddingX: "px-6 sm:px-10 lg:px-12",
      paddingY: "py-12 sm:py-16 lg:py-20",
    },
  },
};

/**
 * Default spacing configuration when none is specified in SiteConfig.
 */
export const DEFAULT_SPACING_CONFIG: GlobalSpacingConfig = {
  useGlobalConfig: true,
  preset: "normal",
};

/**
 * Get spacing classes for a given preset.
 */
export function getSpacingClasses(preset: SpacingPreset): SpacingClasses {
  return SPACING_PRESETS[preset];
}

/**
 * Get spacing classes from a spacing config object.
 */
export function getSpacingFromConfig(
  config?: GlobalSpacingConfig
): SpacingClasses {
  const resolvedConfig = config || DEFAULT_SPACING_CONFIG;
  return getSpacingClasses(resolvedConfig.preset);
}

/**
 * Check if a preset is valid.
 */
export function isValidPreset(preset: string): preset is SpacingPreset {
  return preset in SPACING_PRESETS;
}
